
import { defineComponent, ref, reactive, watch, computed, onMounted, onBeforeUnmount, Ref } from 'vue';
import PlayIcon from '@/ui/components/bases/icons/PlayIcon.vue';
import DownloadIcon from '@/ui/components/bases/icons/DownloadIcon.vue';
import { useRouter } from 'vue-router';
import { Game, GameState } from '../../typings/games';
import * as Games from '../../domain/Games';
import { cefRequest, requestGetter, requestAction } from "../../utils/cef";
import { ui, displayErrors, resetLaunch, setLaunchState } from "../../domain/ui";
import store from "../../store";

let ageWarningShown = false;

const __default__ = defineComponent({
  name: 'GamePage',
  components: {
    PlayIcon,
    DownloadIcon,
  },
  props: {
    id: { type: String, required: true },
  },
  emits: ['openSettings'],
  mounted() {
    requestGetter("IS_LOGGED", {}, (logdata) => {
      if (logdata.status !== 200) {
        this.$router.push({ name: "Login" });
      }
    });
  },
  setup(props, { emit }) {
    const gameState: GameState = reactive({
      blogPosts: null,
      installed: false,
      launchable: false,
      startup: false,
      progress: null,
      selectedVersion: null,
      allVersions: null,
      onlineConfig: null,
      downloadingSpeed: '',
    });
    const data: Ref<Game | null> = ref<Game | null>(null);
    const router = useRouter();

    const accounts = ref<{ name: string; uuid: string }[]>([]);
    const currentUuid = ref('');
    const currentName = ref('');
    const userMenuOpen = ref(false);
    const distMenuOpen = ref(false);
    const switchingUuid = ref('');
    const switchSpin = ref(0);
    let spinRaf = 0;
    const startSpin = () => {
      if (spinRaf) {
        return;
      }
      const loop = (ts: number) => {
        switchSpin.value = (ts * 0.5) % 360;
        spinRaf = requestAnimationFrame(loop);
      };
      spinRaf = requestAnimationFrame(loop);
    };
    const stopSpin = () => {
      if (spinRaf) {
        cancelAnimationFrame(spinRaf);
        spinRaf = 0;
      }
    };

    const currentAccount = computed(() => accounts.value.find((account) => account.uuid === currentUuid.value) || null);
    const otherAccounts = computed(() => accounts.value.filter((account) => account.uuid !== currentUuid.value));
    const displayName = computed(() => (currentAccount.value ? currentAccount.value.name : currentName.value));
    const currentSkin = computed(() => (currentUuid.value ? headUrl(currentUuid.value) : ''));
    const hasMultipleDist = computed(() => !!(gameState.allVersions && gameState.allVersions.length > 1));

    const headUrl = (uuid: string) => `https://crafthead.net/helm/${uuid}?scale=8`;
    const headError = (event: Event) => {
      (event.target as HTMLElement).style.visibility = 'hidden';
    };

    const loadAccounts = () => {
      requestGetter('ACCOUNTS', undefined, (r: any) => {
        if (r.status === 200 && r.payload) {
          accounts.value = Object.values(r.payload).map((account: any) => ({ name: account.name, uuid: account.uuid }));
        }
      });
      requestGetter('UUID', undefined, (r: any) => {
        if (r.status === 200) {
          currentUuid.value = r.payload.uuid;
        }
      });
      requestGetter('NAME', undefined, (r: any) => {
        if (r.status === 200) {
          currentName.value = r.payload.name;
        }
      });
    };

    const closeMenus = () => {
      userMenuOpen.value = false;
      distMenuOpen.value = false;
    };
    const toggleUserMenu = () => {
      const open = userMenuOpen.value;
      closeMenus();
      userMenuOpen.value = !open;
    };
    const toggleDistMenu = () => {
      const open = distMenuOpen.value;
      closeMenus();
      distMenuOpen.value = !open;
    };
    const openSettings = () => {
      closeMenus();
      emit('openSettings', data.value ? data.value.id : '');
    };
    const openAccounts = () => {
      closeMenus();
      emit('openSettings', 'accounts');
    };
    const switchAccount = (uuid: string) => {
      if (!uuid || switchingUuid.value) {
        return;
      }
      switchingUuid.value = uuid;
      startSpin();
      requestAction('SWITCH_ACCOUNT', { uuid }, (r: any) => {
        if (r.status === 200) {
          store.commit('setGames', null);
          window.location.hash = '#/splash';
          window.location.reload();
        } else {
          switchingUuid.value = '';
          stopSpin();
        }
      });
    };
    const chooseDistribution = (option: any) => {
      closeMenus();
      selectedVersionChange(option.id);
    };

    const progress = computed(() => (data.value && ui.launch.gameId === data.value.id ? ui.launch.progress : null));
    const downloadingSpeed = computed(() => (data.value && ui.launch.gameId === data.value.id ? ui.launch.speed : ''));

    // The native launcher declares its phase explicitly via window.launcherState, so we display it
    // as-is. The progress-based derivation below is only a fallback for an older launcher still
    // speaking the legacy setProgress/downloadingSpeed signals.
    const declaredPhase = computed(() => (data.value && ui.launch.gameId === data.value.id ? ui.launch.phase : null));
    const declaredDetail = computed(() => (data.value && ui.launch.gameId === data.value.id ? ui.launch.detail : ''));

    const launchActive = computed(() => gameState.startup || progress.value !== null);
    const launchProgress = computed(() => (progress.value === null ? 0 : progress.value));
    const launchPhase = computed(() => {
      switch (declaredPhase.value) {
        case 'ERROR': return 'error';
        case 'READY': return 'ready';
        case 'LAUNCHING': return 'launch';
        case 'DOWNLOADING': return 'download';
        case 'VERIFYING': return 'verify';
        case 'PREPARING': return 'prepare';
      }
      const p = progress.value;
      if (p !== null && p >= 100) return 'launch';
      if (p !== null && p >= 50) return 'download';
      if (p !== null && p >= 15) return 'verify';
      return 'prepare';
    });
    // Error state declared by the launcher (window.launcherState({ phase:'ERROR', step, code, message, … })).
    const launchError = computed(() => (data.value && ui.launch.gameId === data.value.id ? ui.launch.error : null));
    const STEP_CODE_INDEX: Record<string, number> = { PREPARING: 0, VERIFYING: 1, DOWNLOADING: 2, LAUNCHING: 3 };
    const errorStepIndex = computed(() => {
      const err = launchError.value;
      return err && STEP_CODE_INDEX[err.step] !== undefined ? STEP_CODE_INDEX[err.step] : 0;
    });
    const errorMessage = computed(() => launchError.value?.message || 'Le lancement a échoué');
    const errorRetryable = computed(() => (launchError.value ? launchError.value.retryable !== false : true));
    const errorLink = computed(() => {
      const err = launchError.value;
      if (!err) {
        return null;
      }
      switch (err.code) {
        case 'OUT_OF_MEMORY':
          return { label: 'Ouvrir les paramètres', external: false, action: () => data.value && emit('openSettings', data.value.id) };
        case 'ACCOUNT_ERROR':
          return { label: 'Comptes', external: false, action: () => data.value && emit('openSettings', data.value.id) };
        case 'DISTRIBUTION_NOT_FOUND':
        case 'NETWORK_UNREACHABLE':
          return { label: 'Ouvrir un ticket', external: true, action: () => requestAction('OPEN_TICKET', {}) };
        default:
          return null;
      }
    });
    // Typographic 4-step indicator: each step name is underlined by its OWN progress
    // (Préparation · Vérification · Téléchargement · Lancement).
    const STEP_NAMES = ['Préparation', 'Vérification', 'Téléchargement', 'Lancement'];
    const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

    const stepIndex = computed(() => {
      switch (launchPhase.value) {
        case 'ready': return 4;
        case 'launch': return 3;
        case 'download': return 2;
        case 'verify': return 1;
        default: return 0;
      }
    });
    // Progress within the current step, remapped so each step reads as its own 0..100%.
    const stepFraction = computed(() => {
      const p = launchProgress.value;
      switch (launchPhase.value) {
        case 'verify': return clamp01((p - 15) / (50 - 15));
        case 'download': return clamp01((p - 50) / (100 - 50));
        default: return 0;
      }
    });
    // Presentation layer: the launcher can blow through phases in a single frame (e.g. nothing to
    // download) and pushes progress in bursts, so we replay the four steps sequentially with a
    // minimum dwell and an eased fill. Each step is seen filling in turn instead of snapping, and it
    // stays smooth even when the OS forces prefers-reduced-motion (this is JS, not a CSS animation).
    const MIN_STEP_MS = 260;
    const EASE_TAU = 90;
    const isIndeterminateStep = (i: number) => i === 0 || i === 3;
    const disp = reactive<{ index: number; frac: number; done: boolean[]; ready: boolean }>({
      index: 0,
      frac: 0,
      done: [false, false, false, false],
      ready: false,
    });
    // Continuous motion is driven from the rAF loop rather than CSS @keyframes: the launcher's CEF
    // does not tick CSS animations reliably (the determinate fill, driven here in JS, does move), so
    // the indeterminate sweep, the spinner and the dots are animated by updating inline styles.
    const motion = reactive({ spin: 0, sweep: -100 });
    const fillStyle = (step: { indeterminate: boolean; p: number }) =>
      step.indeterminate ? { transform: `translateX(${motion.sweep}%)` } : { '--p': String(step.p) };
    const dispPhase = computed(() => {
      if (launchPhase.value === 'error') {
        return 'error';
      }
      if (disp.ready) {
        return 'ready';
      }
      switch (disp.index) {
        case 1: return 'verify';
        case 2: return 'download';
        case 3: return 'launch';
        default: return 'prepare';
      }
    });
    const stepPct = computed(() => Math.min(99, Math.round(disp.frac * 100)));
    const steps = computed(() => {
      if (launchPhase.value === 'error') {
        const fi = errorStepIndex.value;
        return STEP_NAMES.map((name, i) => ({
          name,
          done: i < fi,
          active: false,
          indeterminate: false,
          error: i === fi,
          p: i <= fi ? 1 : 0,
          readout: 'none',
        }));
      }
      return STEP_NAMES.map((name, i) => {
        const done = disp.ready || disp.done[i];
        const active = !disp.ready && !done && i === disp.index;
        const indeterminate = active && isIndeterminateStep(i);
        let p = 0;
        if (done) {
          p = 1;
        } else if (active && !indeterminate) {
          p = disp.frac;
        }
        const readout = active && !indeterminate ? 'pct' : 'none';
        return { name, done, active, indeterminate, error: false, p, readout };
      });
    });

    let rafId = 0;
    let lastTs = 0;
    let enterTs = 0;
    const resetDisp = () => {
      disp.index = 0;
      disp.frac = 0;
      disp.done = [false, false, false, false];
      disp.ready = false;
      lastTs = 0;
      enterTs = 0;
    };
    const tick = (ts: number) => {
      if (!lastTs) {
        lastTs = ts;
      }
      if (!enterTs) {
        enterTs = ts;
      }
      const dt = Math.min(64, ts - lastTs);
      lastTs = ts;

      motion.spin = (ts * 0.4) % 360;
      motion.sweep = -100 + 364 * ((ts % 1400) / 1400);

      if (launchPhase.value === 'error') {
        rafId = requestAnimationFrame(tick);
        return;
      }

      if (!disp.ready) {
        const i = disp.index;
        if (i >= 4) {
          disp.ready = true;
        } else {
          const backendReady = launchPhase.value === 'ready';
          const backendIndex = backendReady ? 4 : Math.max(0, Math.min(3, stepIndex.value));
          const past = backendReady || backendIndex > i;
          if (!isIndeterminateStep(i)) {
            const target = past ? 1 : stepFraction.value;
            disp.frac += (target - disp.frac) * (1 - Math.exp(-dt / EASE_TAU));
            if (disp.frac > 0.999) {
              disp.frac = 1;
            }
          }
          const filled = isIndeterminateStep(i) || disp.frac >= 0.995;
          if (past && filled && ts - enterTs >= MIN_STEP_MS) {
            disp.done[i] = true;
            disp.index = i + 1;
            disp.frac = 0;
            enterTs = ts;
          }
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    const startLoop = () => {
      if (rafId) {
        return;
      }
      lastTs = 0;
      enterTs = 0;
      rafId = requestAnimationFrame(tick);
    };
    const stopLoop = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
    };
    watch(launchActive, (active) => {
      if (active) {
        resetDisp();
        startLoop();
      } else {
        stopLoop();
        resetDisp();
      }
    }, { immediate: true });
    const downloadSpeed = computed(() => (declaredDetail.value || downloadingSpeed.value || '').trim().replace(/^\(|\)$/g, ''));
    const detailText = computed(() => {
      const detail = (declaredDetail.value || '').trim();
      const legacy = (downloadingSpeed.value || '').trim().replace(/^\(|\)$/g, '');
      switch (dispPhase.value) {
        case 'error': return errorMessage.value;
        case 'ready': return 'Jeu lancé';
        case 'launch': return detail || 'Démarrage du jeu…';
        case 'download': return 'Téléchargement de la mise à jour…';
        case 'verify': return 'Vérification des fichiers…';
        default: return detail || (legacy.toLowerCase().includes('connexion') ? 'Connexion aux services…' : 'Préparation du lancement…');
      }
    });

    const refreshInstalled = () => {
      if (!data.value) {
        return;
      }
      requestGetter("GAME_IS_INSTALLED", { gameId: data.value.id }, (r) => {
        if (r.status === 200) {
          gameState.installed = r.payload.installed;
        }
      });
    };

    const versionsForCurrent = (game: Game): any[] => {
      const versions = Array.isArray(game.versions) ? game.versions.slice() : [];
      if (!versions.some((v: any) => v.id === game.id)) {
        versions.unshift({ id: game.id, value: game.name, icon: game.icon || '', permission: null });
      }
      return versions;
    };

    const loadGame = (id: string) => {
      if (!id) {
        return;
      }

      if (store.state.games !== null) {
        data.value = store.state.games.find((game: Game) => game.id === id) || null;
      }

      // Reset per-game view state (the download progress lives in the shared launch state, keyed by gameId).
      // The selected version must reflect the distribution actually loaded, not default to the first option.
      gameState.selectedVersion = id;
      gameState.allVersions = null;
      gameState.installed = false;

      if (data.value && data.value.versions && data.value.versions.length) {
        gameState.allVersions = versionsForCurrent(data.value);
      }

      refreshInstalled();

      requestGetter("GET_OS", {}, (r) => {
        if (r.status === 200) {
          const isLin = (r.payload.os === "linux");
          gameState.launchable = !(isLin && data.value !== null && !data.value.compatibleLinux);
        }
      });

      requestGetter("IS_LOGGED", {}, (logdata) => {
        if (logdata.status === 200) {
          requestGetter("GET_TOKEN", {}, (ttdata) => {
            if (ttdata.status === 200 && ttdata.payload && ttdata.payload.jwt) {
              Games.getGames(ttdata.payload.jwt).then((games: Game[]) => {
                const refreshed = games.find((game: Game) => game.id === id);
                if (refreshed) {
                  data.value = refreshed;
                  if (refreshed.versions && refreshed.versions.length) {
                    gameState.allVersions = versionsForCurrent(refreshed);
                  }
                  // Warm the distribution manifest while the player reads the page, so Play is faster.
                  if (refreshed.url) {
                    requestAction("PREFETCH_DISTRIBUTION", { url: refreshed.url, distributionToken: refreshed.distributionToken });
                  }
                }
              }).catch(() => undefined);
            }
          });
        }
      });

      if (id === "palanarchy" && !ageWarningShown) {
        ageWarningShown = true;
        displayErrors(
          "Âge minimum conseillé",
          [
            "Avant de commencer à jouer à Palanarchy, veuillez noter que ce jeu contient des éléments qui peuvent ne pas convenir aux personnes de moins de 16 ans.",
            "Si vous avez moins de 16 ans, nous vous conseillons d'attendre",
            "Merci de votre compréhension et amusez-vous bien dans ce monde de chaos !",
          ],
          "J'ai compris !",
          "",
          "CLOSE",
          "",
          false,
        );
      }
    };

    watch(() => props.id, (id) => loadGame(id), { immediate: true });

    watch(() => store.state.games, (games) => {
      if (!games || gameState.startup) {
        return;
      }
      const refreshed = games.find((game: Game) => game.id === props.id);
      if (refreshed) {
        data.value = refreshed;
        if (refreshed.versions && refreshed.versions.length) {
          gameState.allVersions = versionsForCurrent(refreshed);
        }
      }
    });

    let resetTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReset = (ms: number) => {
      if (resetTimer) {
        clearTimeout(resetTimer);
      }
      resetTimer = setTimeout(() => {
        gameState.startup = false;
        resetLaunch();
      }, ms);
    };

    // Reset is driven by the launcher's declared state: on READY (the game window is up) we clear
    // quickly; a plain 100% without a launch (an error/abort) clears sooner; a real LAUNCHING waits
    // for READY with a safety fallback so the UI never stays stuck.
    watch([progress, launchPhase], ([value, phase]) => {
      if (!data.value || ui.launch.gameId !== data.value.id) {
        return;
      }
      if (phase === 'error') {
        return;
      }
      if (phase === 'ready') {
        refreshInstalled();
        scheduleReset(2500);
      } else if (value !== null && value >= 100) {
        refreshInstalled();
        scheduleReset(phase === 'launch' ? 12000 : 4000);
      }
    });

    const actionBtn = async () => {
      if (!data.value) {
        return;
      }

      const gameId = data.value.id;
      if (resetTimer) {
        clearTimeout(resetTimer);
        resetTimer = null;
      }
      gameState.startup = true;
      ui.launch.gameId = gameId;
      // Clear any prior error/state so a retry re-enters the sequence from the start.
      ui.launch.phase = null;
      ui.launch.error = null;
      ui.launch.progress = null;
      resetDisp();

      try {
        const bench = (label: string, ms: number) => {
          const msg = `actionBtn/${label}: ${Math.round(ms)}ms`;
          console.log('[BENCH] ' + msg);
          try { requestAction('BENCH_LOG', { msg }); } catch (e) { /* ignore */ }
        };
        const tStart = performance.now();

        let t = performance.now();
        const logged = await cefRequest("GETTER", "IS_LOGGED", {});
        bench('IS_LOGGED', performance.now() - t);
        if (logged.status !== 200) {
          gameState.startup = false;
          return;
        }

        t = performance.now();
        const tokenResponse = await cefRequest("GETTER", "GET_TOKEN", {});
        bench('GET_TOKEN', performance.now() - t);
        if (tokenResponse.status !== 200 || !tokenResponse.payload || !tokenResponse.payload.jwt) {
          setLaunchState({ phase: 'ERROR', step: 'PREPARING', code: 'NETWORK_UNREACHABLE', message: 'Impossible de contacter les serveurs de Paladium, veuillez réessayer.' });
          return;
        }

        // The game (with its url/settingsId/distributionToken) is already loaded on the page by
        // getGames, and the distribution manifest is kept warm by the prefetch, so we launch with the
        // data in hand instead of re-fetching it here (that round-trip cost ~3.6s on the critical path).
        t = performance.now();
        const launch = await cefRequest("ACTION", "LAUNCH_GAME", {
          gameId: data.value.id,
          url: data.value.url,
          settingsId: data.value.settingsId,
          distributionToken: data.value.distributionToken,
        });
        bench('LAUNCH_GAME-return', performance.now() - t);
        bench('TOTAL-click-to-launch-return', performance.now() - tStart);

        if (launch.status === 200) {
          gameState.installed = true;
        } else {
          gameState.startup = false;
          resetLaunch();
        }
      } catch (e) {
        setLaunchState({ phase: 'ERROR', step: 'PREPARING', code: 'UNKNOWN', message: 'Le lancement du jeu a échoué, veuillez réessayer.' });
      }
    };

    const dismissLaunch = () => {
      gameState.startup = false;
      resetLaunch();
      resetDisp();
    };

    onMounted(() => {
      loadAccounts();
      window.addEventListener('click', closeMenus);
    });

    onBeforeUnmount(() => {
      stopLoop();
      stopSpin();
      window.removeEventListener('click', closeMenus);
      if (resetTimer) {
        clearTimeout(resetTimer);
      }
    });

    const selectedVersionChange = async (newVersion: any) => {
      gameState.selectedVersion = newVersion;
      const newGameId = typeof newVersion === 'string' ? newVersion : newVersion?.id;
      if (newGameId && data.value && data.value.id !== newGameId) {
        router.push(`/${newGameId}`);
      }
    };

    return {
      data,
      gameState,
      accounts,
      otherAccounts,
      displayName,
      currentSkin,
      hasMultipleDist,
      userMenuOpen,
      distMenuOpen,
      switchingUuid,
      switchSpin,
      headUrl,
      headError,
      toggleUserMenu,
      toggleDistMenu,
      openSettings,
      openAccounts,
      switchAccount,
      chooseDistribution,
      launchActive,
      dispPhase,
      steps,
      stepPct,
      motion,
      fillStyle,
      downloadSpeed,
      detailText,
      errorRetryable,
      errorLink,
      dismissLaunch,
      selectedVersionChange,
      actionBtn,
    };
  },
});

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "1e53cb74": (_ctx.data?.colors?.button?.default),
  "f40121a2": (_ctx.data?.colors?.button?.hover),
  "d43628ae": (_ctx.data?.colors?.button?.disabled)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__